import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { householdService } from '../../services/householdService';
import { groupApplicationService } from '../../services/groupApplicationService';

const UserDetail = ({ user, roles }) => {
  const [households, setHouseholds] = useState([]);
  const [loadingHouseholds, setLoadingHouseholds] = useState(false);
  const [groupApplications, setGroupApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  const loadHouseholds = useCallback(async () => {
    try {
      setLoadingHouseholds(true);
      const householdPromises = user.householdIds.map(id => 
        householdService.getHouseholdById(id)
      );
      const householdData = await Promise.all(householdPromises);
      setHouseholds(householdData);
    } catch (err) {
      console.error('Error loading households:', err);
    } finally {
      setLoadingHouseholds(false);
    }
  }, [user.householdIds]);

  const loadGroupApplications = useCallback(async () => {
    try {
      setLoadingApplications(true);
      const applications = await groupApplicationService.getApplicationsByUser(user.id);
      setGroupApplications(applications);
    } catch (err) {
      console.error('Error loading group applications:', err);
    } finally {
      setLoadingApplications(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (user.householdIds && user.householdIds.length > 0) {
      loadHouseholds();
    }
  }, [user.householdIds, loadHouseholds]);

  useEffect(() => {
    if (user.id) {
      loadGroupApplications();
    }
  }, [user.id, loadGroupApplications]);

  const getRoleNames = (roleIds) => {
    if (!roleIds || !roles.length) return [];
    return roleIds
      .map(roleId => roles.find(role => role.id === roleId)?.name)
      .filter(Boolean);
  };

  const formatAddress = (street, city, zip) => {
    const parts = [street, city, zip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString();
  };

  const getDisplayName = (user) => {
    const name = user.preferredName || user.firstName;
    const lastNameInitial = user.lastName ? user.lastName.substring(0, 1) : '';
    const graduationYear = user.graduationYear || '';
    return `${name} ${lastNameInitial}. ${graduationYear}`;
  };

  const getApplicationStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'TEAM_SUPERVISOR_APPROVED':
      case 'PRIMARY_CAREGIVER_APPROVED':
      case 'COACH_STAFF_APPROVED':
        return 'info';
      case 'TEAM_SUPERVISOR_REJECTED':
      case 'PRIMARY_CAREGIVER_REJECTED':
      case 'COACH_STAFF_REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getApplicationStatusLabel = (status) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="h6" gutterBottom>
        {getDisplayName(user)}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {user.firstName} {user.lastName}
        {user.preferredName && user.preferredName !== user.firstName && (
          <span> (Prefers: {user.preferredName})</span>
        )}
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Email:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="body2" fontWeight="medium">
              Status:
            </Typography>
            <Chip
              label={user.isActive ? 'Active' : 'Inactive'}
              color={user.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="body2" fontWeight="medium">
              Households:
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <HomeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {user.householdIds ? user.householdIds.length : 0}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Roles Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon />
          Roles & Permissions
        </Typography>
        
        <Box display="flex" flexWrap="wrap" gap={1}>
          {getRoleNames(user.roleIds).map((roleName) => (
            <Chip
              key={roleName}
              label={roleName}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
          {getRoleNames(user.roleIds).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No roles assigned
            </Typography>
          )}
        </Box>
      </Box>

      {/* League Enrollment Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupsIcon />
          League Enrollment ({user.groups ? user.groups.length : 0})
        </Typography>
        
        {user.groups && user.groups.length > 0 ? (
          <Grid container spacing={2}>
            {user.groups.map((group) => (
              <Grid item xs={12} md={6} key={group.id}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight="medium" gutterBottom>
                        {group.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Code: {group.code}
                      </Typography>
                      {group.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                          {group.description}
                        </Typography>
                      )}
                      {group.groupType && (
                        <Typography variant="body2" color="text.secondary">
                          Type: {group.groupType.name}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                      <Chip
                        label={group.isActive ? 'Active' : 'Inactive'}
                        color={group.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      {group.seekingMembers && (
                        <Chip
                          label="Seeking Members"
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No league enrollments
          </Typography>
        )}
      </Box>

      {/* Group Applications Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PendingIcon />
          Group Applications ({groupApplications.length})
        </Typography>
        
        {loadingApplications ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : groupApplications.length > 0 ? (
          <Grid container spacing={2}>
            {groupApplications.map((application) => (
              <Grid item xs={12} md={6} key={application.id}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight="medium" gutterBottom>
                        {application.group?.name || 'Unknown Group'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Code: {application.group?.code || 'N/A'}
                      </Typography>
                      {application.group?.groupType && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Type: {application.group.groupType.name}
                        </Typography>
                      )}
                      {application.reason && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Reason: {application.reason}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Applied: {formatDate(application.createdAt)}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                      <Chip
                        label={getApplicationStatusLabel(application.status)}
                        color={getApplicationStatusColor(application.status)}
                        size="small"
                      />
                      {application.applicationType && (
                        <Chip
                          label={application.applicationType.replace(/_/g, ' ')}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No group applications
          </Typography>
        )}
      </Box>

      {/* Households Section */}
      {user.householdIds && user.householdIds.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HomeIcon />
            Households ({user.householdIds.length})
          </Typography>
          
          {loadingHouseholds ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {households.map((household) => (
                <Grid item xs={12} md={6} key={household.id}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                          {household.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Code: {household.householdCode}
                        </Typography>
                        {household.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                            {household.description}
                          </Typography>
                        )}
                        {household.addressStreet && (
                          <Typography variant="body2" color="text.secondary">
                            {formatAddress(household.addressStreet, household.addressCity, household.addressZip)}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={household.isActive ? 'Active' : 'Inactive'}
                        color={household.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* School Information Section */}
      {(user.school || user.graduationYear || user.district) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            School Information
          </Typography>
          
          <Grid container spacing={2}>
            {user.school && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  School:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.school}
                </Typography>
              </Grid>
            )}
            {user.district && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  District:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.district}
                </Typography>
              </Grid>
            )}
            {user.graduationYear && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Graduation Year:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.graduationYear}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Address Information Section */}
      {user.primaryAddressStreet && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon />
            Address Information
          </Typography>
          
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Primary Address:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatAddress(
                user.primaryAddressStreet,
                user.primaryAddressCity,
                user.primaryAddressZip
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Demographics Section */}
      {(user.gender || user.tshirtSize) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Demographics
          </Typography>
          
          <Grid container spacing={2}>
            {user.gender && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Gender:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.gender.replace('_', ' ')}
                </Typography>
              </Grid>
            )}
            {user.tshirtSize && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  T-shirt Size:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.tshirtSize}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Preferences Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preferences & Settings
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center">
              {user.seekingTeamMembers ? (
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <CancelIcon color="disabled" sx={{ mr: 1 }} />
              )}
              <Typography variant="body2">
                <strong>Seeking Team:</strong> {user.seekingTeam ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center">
              {user.optInCommunications ? (
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <CancelIcon color="disabled" sx={{ mr: 1 }} />
              )}
              <Typography variant="body2">
                <strong>Communications Opt-in:</strong> {user.optInCommunications ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* System Information */}
      <Box>
        <Typography variant="h6" gutterBottom>
          System Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Created:</strong> {formatDate(user.createdAt)}
            </Typography>
          </Grid>
          
          {user.updatedAt && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Last Updated:</strong> {formatDate(user.updatedAt)}
              </Typography>
            </Grid>
          )}
          
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>User ID:</strong> {user.id}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default UserDetail; 